"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { createBreakpoint } from "react-use";
import { useQueries } from "@tanstack/react-query";
import Image from "next/image";
import type { ApiWaveCurationGroup } from "@/generated/models/ApiWaveCurationGroup";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import type { CommonSelectItem } from "@/components/utils/select/CommonSelect";
import CommonDropdown from "@/components/utils/select/dropdown/CommonDropdown";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { commonApiFetch } from "@/services/api/common-api";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
interface WaveLeaderboardCurationGroupSelectProps {
  readonly groups: readonly ApiWaveCurationGroup[];
  readonly selectedGroupId: string | null;
  readonly onChange: (groupId: string | null) => void;
  readonly mode?: WaveLeaderboardCurationGroupSelectMode;
}
const useBreakpoint = createBreakpoint({
  MD: 768,
  S: 0
});
type WaveLeaderboardCurationGroupSelectMode = "auto" | "tabs" | "dropdown";
export function WaveLeaderboardCurationGroupSelect(t0) {
  const $ = _c(21);
  const {
    groups,
    selectedGroupId,
    onChange,
    mode: t1
  } = t0;
  const mode = t1 === undefined ? "auto" : t1;
  const breakpoint = useBreakpoint();
  const isSmallViewport = breakpoint === "S";
  let resolvedMode = mode;
  if (mode === "auto") {
    resolvedMode = isSmallViewport ? "dropdown" : "tabs";
  }
  let t2;
  if ($[0] !== groups) {
    t2 = groups.map(_temp);
    $[0] = groups;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== groups) {
    t3 = results => {
      const map = new Map();
      groups.forEach((group_0, i) => {
        const pfp = results[i]?.data?.created_by.pfp;
        if (pfp) {
          map.set(group_0.id, pfp);
        }
      });
      return map;
    };
    $[2] = groups;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      queries: t2,
      combine: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const pfpMap = useQueries(t4);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      key: "all-submissions",
      label: "All submissions",
      value: null
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== groups) {
    t6 = [t5, ...groups.map(_temp2)];
    $[8] = groups;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const items = t6;
  if (groups.length === 0) {
    return null;
  }
  if (resolvedMode === "dropdown") {
    let t7;
    if ($[10] !== items || $[11] !== onChange || $[12] !== selectedGroupId) {
      t7 = <div className="tw-min-w-0"><CommonDropdown items={items} activeItem={selectedGroupId} filterLabel="Group" setSelected={onChange} size="sm" showFilterLabel={true} /></div>;
      $[10] = items;
      $[11] = onChange;
      $[12] = selectedGroupId;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  let t7;
  if ($[14] !== selectedGroupId) {
    t7 = value => {
      if (selectedGroupId === value) {
        return "tw-flex tw-items-center tw-gap-x-1.5 tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-rounded-lg tw-transition-colors tw-whitespace-nowrap tw-border-0 tw-ring-1 tw-ring-inset tw-ring-iron-600 tw-bg-iron-800 tw-text-iron-200";
      }
      return "tw-flex tw-items-center tw-gap-x-1.5 tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-rounded-lg tw-transition-colors tw-whitespace-nowrap tw-border-0 tw-ring-1 tw-ring-inset tw-ring-transparent tw-bg-transparent tw-text-iron-500 desktop-hover:hover:tw-ring-iron-700 desktop-hover:hover:tw-text-iron-300";
    };
    $[14] = selectedGroupId;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const getTabClassName = t7;
  let t8;
  if ($[16] !== getTabClassName || $[17] !== items || $[18] !== onChange || $[19] !== pfpMap) {
    t8 = <div data-testid="curation-group-select" className="tw-flex tw-items-center tw-gap-x-1">{items.map(item => {
        const pfp_0 = item.value ? pfpMap.get(item.value) : null;
        return <button key={item.key} type="button" className={getTabClassName(item.value)} onClick={() => onChange(item.value)}>{pfp_0 && <Image src={getScaledImageUri(pfp_0, ImageScale.W_AUTO_H_50)} alt="" width={16} height={16} className="tw-size-4 tw-flex-shrink-0 tw-rounded-md tw-object-cover" />}{item.label}</button>;
      })}</div>;
    $[16] = getTabClassName;
    $[17] = items;
    $[18] = onChange;
    $[19] = pfpMap;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function _temp2(group_1) {
  return {
    key: group_1.id,
    label: group_1.name,
    value: group_1.id as string | null
  };
}
function _temp(group) {
  return {
    queryKey: [QueryKey.GROUP, group.group_id],
    queryFn: async () => await commonApiFetch({
      endpoint: `groups/${group.group_id}`
    }),
    enabled: Boolean(group.group_id),
    staleTime: 300000
  };
}